import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, setDoc, collection, addDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase'; // adjust to your project path
import './BuyCredits.css';

// CONSTANTS
const ORIGINAL_PRICE = 49;
const PROMO_PRICE = 25;
const TAX = 0.2; // 20% KDV
const DEADLINE_DATE = new Date('2026-04-14T23:59:59'); // End of day April 15, 2026
const EXCHANGE_RATE = 44.75; // Hardcoded exchange rate

const COMPANY_NAME = 'IDEATRUM YAPAY ZEKA MÜHENDİSLİK ARGE YAZILIM VE DANIŞMANLIK HİZMETLERİ TİCARET LİMİTED ŞİRKETİ';
const COMPANY_ACCOUNT_NUMBER = 'TR82 0006 2001 5960 0006 2979 14'; 

const BuyCredits = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user_idx } = location.state || {};

  const [creditCount, setCreditCount] = useState(1);
  const [discountCoupon, setDiscountCoupon] = useState("");
  const [referenceCode, setReferenceCode] = useState(null);
  
  // New States for Flow and Invoice
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [ordered, setOrdered] = useState(false);
  const [invoiceType, setInvoiceType] = useState('tuzel'); // 'tuzel' or 'sahis'
  const [invoiceData, setInvoiceData] = useState({
    firmaAdi: '',
    adres: '',
    telefon: '',
    vergiDairesi: '',
    vergiNo: ''
  });
  
  // New State for Timer
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [isPromoActive, setIsPromoActive] = useState(true);


  // NEW: Track Page View grouped by User
  useEffect(() => {
    const trackVisit = async () => {
      if (!user_idx) return; 

      try {
        // Point specifically to a document named after the user_idx
        const userTrackingRef = doc(db, 'buy_credit_views', user_idx);
        
        // Use setDoc with { merge: true } so we don't overwrite existing data
        await setDoc(userTrackingRef, {
          user_idx: user_idx, // Good to keep inside the doc for easy querying
          buy_credits_visits: arrayUnion(new Date().toISOString()) 
        }, { merge: true });

      } catch (err) {
        console.error("Failed to track page view:", err);
      }
    };

    trackVisit();
  }, [user_idx]);


  // Check Date and Calculate Timer
  useEffect(() => {
    const calculateTime = () => {
      const now = new Date();
      const difference = DEADLINE_DATE - now;

      if (difference > 0) {
        setIsPromoActive(true);
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        setDaysRemaining(days);
      } else {
        setIsPromoActive(false);
        setDaysRemaining(0);
      }
    };

    calculateTime();
    // Optional: Update every minute to stay accurate if user keeps page open long
    const timer = setInterval(calculateTime, 60000); 
    return () => clearInterval(timer);
  }, []);

  // Determine current Unit Price based on date
  const currentUnitPrice = isPromoActive ? PROMO_PRICE : ORIGINAL_PRICE;

  const formatDateTR = (date) =>
    date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const getDiscountRateFromCode = (code) => {
    const match = code.trim().match(/(\d{2})$/); // matches last 2 digits
    if (!match) return 0;

    const digits = parseInt(match[1], 10);
    if (isNaN(digits) || digits <= 0 || digits > 99) return 0;

    return digits / 100; // convert to decimal percentage
  };

  const discountRate = getDiscountRateFromCode(discountCoupon);
  const basePrice = creditCount * currentUnitPrice;
  const priceWithTax = basePrice * (1 + TAX);
  
  // Apply coupon discount to the taxed price
  const discountAmount = priceWithTax * discountRate;
  const discountedTotal = priceWithTax - discountAmount;
  const totalPriceTL = discountedTotal * EXCHANGE_RATE;

  const generateReferenceCode = () => {
    return `REF-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
  };

  const handleInvoiceChange = (e) => {
    setInvoiceData({ ...invoiceData, [e.target.name]: e.target.value });
  };

  const handleVergiNoChange = (e) => {
    // Only allow numbers for Vergi No / TC No
    const val = e.target.value.replace(/\D/g, ''); 
    const maxLen = invoiceType === 'tuzel' ? 10 : 11;
    
    if (val.length <= maxLen) {
      setInvoiceData({ ...invoiceData, vergiNo: val });
    }
  };

  const handleInvoiceTypeChange = (type) => {
    setInvoiceType(type);
    // Optional: Clear fields when switching types so old data doesn't conflict
    setInvoiceData({
      ...invoiceData,
      firmaAdi: '',
      vergiDairesi: '',
      vergiNo: ''
    });
  };

  const handleOrder = async () => {
    if (!user_idx) {
      alert("User ID not provided.");
      return;
    }

    const ref = generateReferenceCode();
    setReferenceCode(ref);

    const orderDoc = {
      userId: user_idx,
      credits: creditCount,
      discountCoupon: discountCoupon,
      unitPrice: currentUnitPrice, // Saving which price point was used
      totalPrice: discountedTotal,
      totalPriceTL: totalPriceTL, // Saving TL calculation
      reference: ref,
      createdAt: new Date(),
      status: "order",
      invoiceType: invoiceType // Optional: Save the type chosen just for reference
    };

    try {
      // 1. Save the primary order document
      await setDoc(
        doc(db, 'credit_orders', user_idx, 'orders', ref),
        orderDoc
      );

      // 2. Save the invoice data to the subcollection
      await setDoc(
        doc(db, 'credit_orders', user_idx, 'orders', ref, 'invoice_data', 'details'),
        {
          ...invoiceData,
          createdAt: new Date()
        }
      );

      setOrdered(true);
    } catch (err) {
      console.error("Error writing to Firestore:", err);
      if (err.code === 'permission-denied') {
        alert("Geçersiz indirim kodu veya yetki hatası.");
      }
    }
  };

  return (
    <div className="buy-credits-container">
      <div className="buy-credits-box">
        <h2>Proje Kredisi Alımı</h2>

        {!ordered ? (
          !showInvoiceForm ? (
            /* --- STEP 1: PRICING & CREDIT SELECTION --- */
            <>
              {/* PROMO BANNER */}
              {isPromoActive && (
                <div style={{ 
                  backgroundColor: '#ffebee',
                  color: '#8e1b1b',
                  padding: '12px 12px',
                  borderRadius: '10px',
                  marginBottom: '15px',
                  textAlign: 'center',
                  border: '1px solid #ef9a9a'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                    <span style={{
                      background: '#d32f2f',
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: '0.85em',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      letterSpacing: '0.3px'
                    }}>
                      SON ŞANS
                    </span>
                    <span style={{
                      background: '#ffffff',
                      color: '#b71c1c',
                      fontWeight: 800,
                      fontSize: '0.85em',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      border: '1px solid #ef9a9a',
                      letterSpacing: '0.3px'
                    }}>
                      UZATILDI
                    </span>
                  </div>
                  <div style={{ fontWeight: 800, marginBottom: '4px' }}>
                    Kampanya süresi <span style={{ textDecoration: 'underline' }}>{formatDateTR(DEADLINE_DATE)}</span> tarihine kadar uzatıldı.
                  </div>
                  <div style={{ fontSize: '0.95em' }}>
                    İndirimin bitimine kalan süre: <strong>{daysRemaining} gün</strong>
                  </div>
                </div>
              )}

              <div className="price-display">
                <p>Kredi Başı Ücret (Kavram Oluşturma & Proje Yazımı & 1 Ay Sınır YZ Desteği):</p>
                
                {isPromoActive ? (
                   <p style={{ fontSize: '1.2em' }}>
                     <span style={{ textDecoration: 'line-through', color: '#888', marginRight: '10px' }}>
                       ${ORIGINAL_PRICE}
                     </span>
                     <strong style={{ color: '#d32f2f', fontSize: '1.3em' }}>
                       ${PROMO_PRICE}
                     </strong> 
                     <span style={{ fontSize: '0.9em' }}> (+ %20 KDV)</span>
                   </p>
                ) : (
                  <p><strong>${ORIGINAL_PRICE}</strong> (+ %20 KDV)</p>
                )}
              </div>

              <div className="credit-selector">
                <label htmlFor="credits">Kredi Adedi:</label>
                <input
                  type="number"
                  id="credits"
                  min="1"
                  value={creditCount}
                  onChange={(e) => setCreditCount(parseInt(e.target.value) || 1)}
                />
              </div>
              <div className="credit-selector">
                <label htmlFor="discount">İndirim Kodu:</label>
                <input
                  type="text"
                  id="discount"
                  value={discountCoupon}
                  placeholder="Varsa kodunuzu giriniz"
                  onChange={(e) => setDiscountCoupon(e.target.value || "")}
                />
              </div>

              <hr />

              <p>Ara Toplam: <strong>${basePrice.toFixed(2)}</strong></p>
              <p>KDV (%20): <strong>${(basePrice * TAX).toFixed(2)}</strong></p>
              
              {discountRate > 0 && (
                <p style={{ color: 'green' }}>
                  Ekstra İndirim Kodu (%{(discountRate * 100).toFixed(0)}): -${discountAmount.toFixed(2)}
                </p>
              )}

              <p style={{ fontSize: '1.2em', marginTop: '10px' }}>
                Ödenecek Toplam Tutar: <strong>${discountedTotal.toFixed(2)}</strong>
              </p>
              
              <p style={{ fontSize: '1.2em', color: '#555' }}>
                Ödenecek Toplam Tutar (TL): <strong>{totalPriceTL.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL</strong>
              </p>
              
              <p><br/></p>
              <p>Ek bilgi ve sorularınız için <strong>info@ideatrum.com</strong></p>

              <div className="buycredit-confirm-buttons">
                {/* Switch to Invoice Form view */}
                <button onClick={() => setShowInvoiceForm(true)}>SİPARİŞİ TAMAMLA</button>
                <button onClick={() => navigate(-1)} style={{backgroundColor: '#666'}}>İPTAL</button>
              </div>
            </>
          ) : (
            /* --- STEP 2: INVOICE DETAILS FORM --- */
            <div className="invoice-form-section">
              <h3>Fatura Bilgileri</h3>
              <p style={{ marginBottom: '15px', color: '#555', fontSize: '0.9em' }}>
                Faturanızın kesilebilmesi için lütfen bilgileri eksiksiz doldurun.
              </p>

              {/* Toggle Buttons for Invoice Type */}
              <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', justifyContent: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="invoiceType" 
                    value="tuzel" 
                    checked={invoiceType === 'tuzel'} 
                    onChange={() => handleInvoiceTypeChange('tuzel')} 
                  />
                  Kurumsal (Tüzel)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="invoiceType" 
                    value="sahis" 
                    checked={invoiceType === 'sahis'} 
                    onChange={() => handleInvoiceTypeChange('sahis')} 
                  />
                  Bireysel (Şahıs)
                </label>
              </div>

              <div className="credit-selector" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                
                <textarea 
                  name="firmaAdi" 
                  placeholder={invoiceType === 'tuzel' ? "Firma Adı" : "Ad Soyad"} 
                  value={invoiceData.firmaAdi} 
                  onChange={handleInvoiceChange} 
                  style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc', resize: 'vertical', minHeight: '40px' }} 
                  required 
                />
                
                <textarea 
                  name="adres" 
                  placeholder="Adres" 
                  value={invoiceData.adres} 
                  onChange={handleInvoiceChange} 
                  style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc', resize: 'vertical', minHeight: '60px' }} 
                  required 
                />
                
                <input type="text" name="telefon" placeholder="Telefon" value={invoiceData.telefon} onChange={handleInvoiceChange} required />
                
                {invoiceType === 'tuzel' && (
                  <input 
                    type="text" 
                    name="vergiDairesi" 
                    placeholder="Vergi Dairesi" 
                    value={invoiceData.vergiDairesi} 
                    onChange={handleInvoiceChange} 
                    required 
                  />
                )}
                
                <input 
                  type="text" 
                  name="vergiNo" 
                  placeholder={invoiceType === 'tuzel' ? "Vergi No (10 Haneli)" : "TC Kimlik No (11 Haneli)"} 
                  value={invoiceData.vergiNo} 
                  onChange={handleVergiNoChange} 
                  maxLength={invoiceType === 'tuzel' ? 10 : 11}
                  required 
                />
              </div>

              <div className="buycredit-confirm-buttons">
                {/* Final Submission */}
                <button onClick={handleOrder}>ONAYLA VE SİPARİŞİ BİTİR</button>
                <button onClick={() => setShowInvoiceForm(false)} style={{backgroundColor: '#666'}}>GERİ DÖN</button>
              </div>
            </div>
          )
        ) : (
          /* --- STEP 3: PAYMENT INSTRUCTIONS --- */
          <div className="payment-instructions">
            <h3>Ödeme Talimatları</h3>
            <p>Lütfen aşağıdaki banka hesabına toplam tutarı havale ediniz:</p>
            <p><strong>Ünvan:</strong> {COMPANY_NAME}</p>
            <p><strong>Hesap Numarası:</strong> {COMPANY_ACCOUNT_NUMBER}</p>
            <p><strong>Referans Kodu:</strong> <span style={{fontSize:'1.2em', color:'blue'}}>{referenceCode}</span></p>
            <p>Ödemenin doğru şekilde eşleştirilebilmesi için bu referans kodunu havale açıklamasına yazınız.</p>
            <p><br/></p>
            <p>Ödeme tamamlandıktan sonra kredi tanımlanması bir kaç saat sürebilir.</p>
            <p><br/></p>
            <p>Ek bilgi ve sorularınız için <strong>info@ideatrum.com</strong></p>

            <div className="buycredit-confirm-buttons">
              <button onClick={() => navigate('/*')}>TAMAMLANDI</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyCredits;